import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ProductService } from 'src/app/core/api/product/product.service';
import { RoutesKeys } from 'src/app/utils/routes';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';

@Component({
  selector: 'app-coupon-card-vip-customer',
  templateUrl: './coupon-card-vip-customer.component.html',
  styleUrls: ['./coupon-card-vip-customer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Coupon VIP card CUSTOMER
 */
export class CouponCardVipCustomerComponent implements OnInit, AfterViewInit {
  @Input() businessName!: string;
  @Input() resellerName!: string;
  @Input() vipLevel!: string;
  @Input() familyId!: number;
  @Input() familyName!: string;
  @Input() discountValue!: number;
  @Input() couponCode!: string;
  @Input() startingDate!: string;
  @Input() expirationDate!: string;
  @Input() business!: any;
  @Input() printers!: any;
  @Input() countryName!: any;
  @Input() couponId!: any;
  @Input() active?: boolean;
  @Input() configSeriesDisclaimer?: string;
  @Input() timelineDisabled?: boolean;
  readonly activeText = 'coupon-expires-on';
  readonly expiredText = 'coupon-expired-on';
  models!: any;
  uniqueModel!: any;
  showInfo = false;
  businessPointsInfo?: any;
  loadingBusinessInfo = false;
  language = '';

  @Input() businessImgBase64?: string;
  /**
   * @constructor
   */
  constructor(
    public readonly translationService: TranslationService,
    public readonly productService: ProductService,
    private readonly elRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly rewardService: RewardService,
    private readonly snackBarService: SnackBarService
  ) {}

  /**
   * on init
   */
  ngOnInit(): void {
    this.productService
      .getBusinessEquipments(this.business?.id)
      .subscribe((equipments) => {
        this.models = equipments.map((obj) => obj.planModel);
        this.findModel();
      });
    this.language = this.translationService.getActiveLang();
  }

  /**
   * Check card footer status
   * @param {any} value
   */
  @Input()
  set clickable(value: any) {
    this._clickable = value !== false && value !== 'false';
  }
  private _clickable = false;

  /**
   * Check card footer status
   * @param {any} value
   */
  @Input()
  set showButtonInfo(value: any) {
    this._showButtonInfo = value !== false && value !== 'false';
  }
  protected _showButtonInfo = false;

  /**
   * afterviewinit
   * If the business does not have image, replace by a svg image corresponding to account_circle mat-icon
   * This replacement is necessary because mat-icon does not work with html-to-image library
   */
  ngAfterViewInit(): void {
    const cardElement: HTMLElement = this.elRef.nativeElement;
    const appRoundedImageElement =
      cardElement.querySelector('app-rounded-image');
    const matIconElement = cardElement.querySelector('mat-icon');
    if (this.business.imgSource === '') {
      const parent = appRoundedImageElement!.parentNode;
      this.renderer.removeChild(parent, matIconElement);

      const imgElement = document.createElement('img');
      imgElement.src = this.convertMatIconToBase64();

      matIconElement!.replaceWith(imgElement);
    }
  }

  /**
   * Check card footer status
   */
  get statusCheck() {
    if (this.active) {
      return this.activeText;
    }
    return this.expiredText;
  }

  /**
   * Convert icon to Base64
   * @return {string}
   */
  private convertMatIconToBase64(): string {
    const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
      <path d="M0 0h24v24H0z" fill="none"/>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10
        10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3
        -3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99
        4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5
        3.22-6 3.22z" fill="#002775"/>
    </svg>
    `;
    return 'data:image/svg+xml;base64,' + window.btoa(svgString);
  }

  /**
   * On route to coupon details page
   * @param {number} customerId
   */
  openCouponDetails(customerId: number) {
    if (!this._clickable || this.showInfo) return;
    this.router.navigate([
      RoutesKeys.CUSTOMER_DETAIL,
      customerId,
      RoutesKeys.COUPONS_VIP,
      this.couponId,
    ]);
  }

  /**
   * Find Model that has discount
   */
  findModel(): void {
    this.uniqueModel = 'SERIES-' + this.familyName + ' INK';
  }

  /**
   * Toggles show info
   * @param {MouseEvent} event
   */
  toggleInfo(event: MouseEvent) {
    event.stopPropagation();
    if (!this.showInfo && !this.businessPointsInfo) {
      this.getBusinessInfo();
    }
    this.showInfo = !this.showInfo;
  }

  /**
   * Turns off show info
   * @param {MouseEvent} event
   */
  closeInfo(event: MouseEvent) {
    event.stopPropagation();
    this.showInfo = false;
  }

  /**
   * Get business information about point summary
   */
  getBusinessInfo() {
    if (!this.businessPointsInfo && !this.loadingBusinessInfo) {
      this.loadingBusinessInfo = true;
      this.rewardService
        .getBusinessPointSummaryByFamily(this.business?.id, this.familyId!)
        .subscribe({
          next: (businessData) => {
            this.businessPointsInfo = businessData;
          },
          complete: () => {
            this.loadingBusinessInfo = false;
          },
          error: () => {
            this.snackBarService.error('unexpected-error');
            this.loadingBusinessInfo = false;
          },
        });
    }
  }
}
